import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import {
  MEMBERSHIP_TIERS,
  normalizeMembershipTier,
} from './membership';

const SAFE_FALLBACK_TIER = MEMBERSHIP_TIERS.CALENDAR;

function toNonEmptyString(value) {
  const text = String(value || '').trim();
  return text || '';
}

function createSafeFallbackAccess(
  fallbackTier = SAFE_FALLBACK_TIER
) {
  const calendarTier =
    MEMBERSHIP_TIERS.CALENDAR;
  const safeTier = normalizeMembershipTier(
    fallbackTier
  );

  // Calendar is the required safe fallback for unresolved org access.
  if (safeTier !== calendarTier) {
    return {
      effectiveTier: calendarTier,
      selectedTier: calendarTier,
      subscriptionStatus: 'active',
      trialStartedAt: null,
      trialEndsAt: null,
      trialDaysRemaining: 0,
      isTrialActive: false,
      trialWarningLevel: 'none',
    };
  }

  return {
    effectiveTier: calendarTier,
    selectedTier: calendarTier,
    subscriptionStatus: 'active',
    trialStartedAt: null,
    trialEndsAt: null,
    trialDaysRemaining: 0,
    isTrialActive: false,
    trialWarningLevel: 'none',
  };
}

function resolveEffectiveOrganisationTier(
  orgBillingRow,
  fallbackTier = SAFE_FALLBACK_TIER,
  now = new Date()
) {
  const safeTier = normalizeMembershipTier(
    fallbackTier
  );

  const selectedTier = normalizeMembershipTier(
    orgBillingRow?.subscription_tier ||
      safeTier
  );

  const subscriptionStatus = String(
    orgBillingRow?.subscription_status ||
      'active'
  )
    .trim()
    .toLowerCase();

  if (subscriptionStatus === 'trialing') {
    const trialEndsAt = new Date(
      orgBillingRow?.trial_ends_at || ''
    );
    const nowDate =
      now instanceof Date
        ? now
        : new Date(now);
    const nowMs = Number.isFinite(
      nowDate.getTime()
    )
      ? nowDate.getTime()
      : Date.now();

    if (
      Number.isFinite(
        trialEndsAt.getTime()
      ) &&
      trialEndsAt.getTime() > nowMs
    ) {
      return MEMBERSHIP_TIERS.FULL_PACKAGE;
    }

    return MEMBERSHIP_TIERS.CALENDAR;
  }

  if (subscriptionStatus === 'active') {
    return selectedTier;
  }

  if (
    subscriptionStatus === 'past_due' ||
    subscriptionStatus === 'cancelled'
  ) {
    return MEMBERSHIP_TIERS.CALENDAR;
  }

  return MEMBERSHIP_TIERS.CALENDAR;
}

function resolveOrganisationTrialDetails(
  orgBillingRow,
  now = new Date()
) {
  const subscriptionStatus = String(
    orgBillingRow?.subscription_status ||
      'active'
  )
    .trim()
    .toLowerCase();

  const trialStartedAt =
    orgBillingRow?.trial_started_at || null;
  const trialEndsAt =
    orgBillingRow?.trial_ends_at || null;

  const trialEndsDate = new Date(
    trialEndsAt || ''
  );
  const hasValidTrialEndDate =
    Number.isFinite(
      trialEndsDate.getTime()
    );
  const nowDate =
    now instanceof Date
      ? now
      : new Date(now);
  const nowMs = Number.isFinite(
    nowDate.getTime()
  )
    ? nowDate.getTime()
    : Date.now();

  const isTrialActive =
    subscriptionStatus === 'trialing' &&
    hasValidTrialEndDate &&
    trialEndsDate.getTime() > nowMs;

  let trialDaysRemaining = 0;

  if (isTrialActive) {
    const msRemaining =
      trialEndsDate.getTime() - nowMs;

    trialDaysRemaining = Math.max(
      1,
      Math.ceil(
        msRemaining /
          (24 * 60 * 60 * 1000)
      )
    );
  }

  let trialWarningLevel = 'none';

  if (isTrialActive) {
    if (trialDaysRemaining >= 1 && trialDaysRemaining <= 3) {
      trialWarningLevel = 'three-days';
    } else if (trialDaysRemaining >= 4 && trialDaysRemaining <= 7) {
      trialWarningLevel = 'seven-days';
    }
  } else if (
    subscriptionStatus === 'trialing'
  ) {
    trialWarningLevel = 'expired';
  }

  return {
    subscriptionStatus,
    trialStartedAt,
    trialEndsAt,
    trialDaysRemaining,
    isTrialActive,
    trialWarningLevel,
  };
}

export async function resolveOrganisationSubscriptionAccess({
  supabaseClient,
  authUserId,
  fallbackTier = SAFE_FALLBACK_TIER,
  now = new Date(),
}) {
  const safeFallback =
    createSafeFallbackAccess(
      fallbackTier
    );
  const userId = toNonEmptyString(authUserId);

  if (!supabaseClient || !userId) {
    return safeFallback;
  }

  try {
    const { data: userRow, error: userError } = await supabaseClient
      .from('users')
      .select('organisation_id, org_name')
      .eq('auth_id', userId)
      .maybeSingle();

    if (userError) {
      return safeFallback;
    }

    const organisationId = toNonEmptyString(
      userRow?.organisation_id
    );
    const orgName = toNonEmptyString(
      userRow?.org_name
    );

    if (!organisationId || !orgName) {
      return safeFallback;
    }

    const {
      data: orgBillingRow,
      error: orgBillingError,
    } = await supabaseClient
      .from('org_billing_settings')
      .select(
        'subscription_tier, subscription_status, trial_started_at, trial_ends_at'
      )
      .eq('org_name', orgName)
      .maybeSingle();

    if (orgBillingError || !orgBillingRow) {
      return safeFallback;
    }

    const selectedTier = normalizeMembershipTier(
      orgBillingRow.subscription_tier ||
        safeFallback.selectedTier
    );

    const effectiveTier =
      resolveEffectiveOrganisationTier(
        orgBillingRow,
        safeFallback.effectiveTier,
        now
      );

    const trialDetails =
      resolveOrganisationTrialDetails(
        orgBillingRow,
        now
      );

    return {
      effectiveTier,
      selectedTier,
      subscriptionStatus:
        trialDetails.subscriptionStatus,
      trialStartedAt:
        trialDetails.trialStartedAt,
      trialEndsAt:
        trialDetails.trialEndsAt,
      trialDaysRemaining:
        trialDetails.trialDaysRemaining,
      isTrialActive:
        trialDetails.isTrialActive,
      trialWarningLevel:
        trialDetails.trialWarningLevel,
    };
  } catch (_error) {
    return safeFallback;
  }
}

export async function resolveOrganisationSubscriptionTier({
  supabaseClient,
  authUserId,
  fallbackTier = SAFE_FALLBACK_TIER,
}) {
  const access =
    await resolveOrganisationSubscriptionAccess({
      supabaseClient,
      authUserId,
      fallbackTier,
    });

  return access.effectiveTier;
}

export function useOrganisationSubscriptionTier(user) {
  const authUserId = toNonEmptyString(user?.id);
  const [subscriptionState, setSubscriptionState] =
    useState({
      authUserId: '',
      access: createSafeFallbackAccess(
        SAFE_FALLBACK_TIER
      ),
      loading: true,
    });
  const hasCurrentUserAccess =
    subscriptionState.authUserId === authUserId;
  const organisationSubscriptionAccess =
    hasCurrentUserAccess
      ? subscriptionState.access
      : createSafeFallbackAccess(
          SAFE_FALLBACK_TIER
        );
  const organisationSubscriptionLoading =
    !hasCurrentUserAccess ||
    subscriptionState.loading;

  useEffect(() => {
    let cancelled = false;

    async function loadOrganisationSubscriptionTier() {
      if (!authUserId) {
        if (!cancelled) {
          setSubscriptionState({
            authUserId: '',
            access: createSafeFallbackAccess(
              SAFE_FALLBACK_TIER
            ),
            loading: false,
          });
        }
        return;
      }

      setSubscriptionState({
        authUserId,
        access: createSafeFallbackAccess(
          SAFE_FALLBACK_TIER
        ),
        loading: true,
      });

      const nextAccess = await resolveOrganisationSubscriptionAccess({
        supabaseClient: supabase,
        authUserId,
      });

      if (!cancelled) {
        setSubscriptionState({
          authUserId,
          access: nextAccess,
          loading: false,
        });
      }
    }

    loadOrganisationSubscriptionTier();

    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  return {
    organisationSubscriptionTier:
      organisationSubscriptionAccess.effectiveTier,
    organisationSubscriptionLoading,
    selectedOrganisationSubscriptionTier:
      organisationSubscriptionAccess.selectedTier,
    organisationSubscriptionStatus:
      organisationSubscriptionAccess.subscriptionStatus,
    organisationTrialStartedAt:
      organisationSubscriptionAccess.trialStartedAt,
    organisationTrialEndsAt:
      organisationSubscriptionAccess.trialEndsAt,
    organisationTrialDaysRemaining:
      organisationSubscriptionAccess.trialDaysRemaining,
    organisationTrialActive:
      organisationSubscriptionAccess.isTrialActive,
    organisationTrialWarningLevel:
      organisationSubscriptionAccess.trialWarningLevel,
  };
}
